import { useEffect, useState } from 'react';
import { Card, Grid, Group, Stack, Text, Title, Skeleton, Alert } from '@mantine/core';
import { AreaChart } from '@mantine/charts';
import { IconAlertCircle } from '@tabler/icons-react';
import { api, type SummaryStats, type EventRow } from '../api';
import { PageHeader } from '../components/PageHeader';
import { MetricTile } from '../components/MetricTile';

// One day, in ms.
const DAY = 86_400_000;

// Aggregate raw events into per-hour buckets for the Traffic chart, and into
// today / yesterday / last-7d counts per metric for the tiles.
function aggregate(events: EventRow[]) {
  const now = Date.now();
  const startToday = new Date(); startToday.setHours(0,0,0,0);
  const startYesterday = startToday.getTime() - DAY;
  const startLast7 = now - 7 * DAY;

  const tiles = {
    clicks:   { today: 0, yesterday: 0, last7: 0, total: 0, series: [] as number[] },
    installs: { today: 0, yesterday: 0, last7: 0, total: 0, series: [] as number[] },
    leads:    { today: 0, yesterday: 0, last7: 0, total: 0, series: [] as number[] },
    ftds:     { today: 0, yesterday: 0, last7: 0, total: 0, series: [] as number[] },
    revenue:  { today: 0, yesterday: 0, last7: 0, total: 0, series: [] as number[] },
  };

  // 7×24 hour buckets for the area chart.
  const buckets: { ts: number; clicks: number; installs: number; leads: number; ftds: number }[] = [];
  const startHour = Math.floor((now - 7 * DAY) / 3_600_000) * 3_600_000;
  for (let t = startHour; t <= now; t += 3_600_000) {
    buckets.push({ ts: t, clicks: 0, installs: 0, leads: 0, ftds: 0 });
  }
  const bucketIdx = (ts: number) => Math.max(0, Math.min(buckets.length - 1, Math.floor((ts - startHour) / 3_600_000)));

  for (const ev of events) {
    const ts = ev.ts;
    let kind: keyof typeof tiles | null = null;
    if (ev.event_type === 'click')   kind = 'clicks';
    else if (ev.event_type === 'install') kind = 'installs';
    else if (ev.event_type === 'lead')    kind = 'leads';
    else if (ev.event_type === 'ftd')     kind = 'ftds';

    if (kind) {
      tiles[kind].total += 1;
      if (ts >= startToday.getTime())              tiles[kind].today += 1;
      else if (ts >= startYesterday && ts < startToday.getTime()) tiles[kind].yesterday += 1;
      if (ts >= startLast7)                         tiles[kind].last7 += 1;

      const b = buckets[bucketIdx(ts)];
      if (b) (b as any)[kind === 'ftds' ? 'ftds' : kind] += 1;
    }

    if (ev.event_type === 'ftd' || ev.event_type === 'deposit') {
      const amt = ev.amount || 0;
      tiles.revenue.total += amt;
      if (ts >= startToday.getTime())              tiles.revenue.today += amt;
      else if (ts >= startYesterday && ts < startToday.getTime()) tiles.revenue.yesterday += amt;
      if (ts >= startLast7)                         tiles.revenue.last7 += amt;
    }
  }

  // Build per-tile sparkline series (8 evenly-spaced sample points across last 7d).
  const sample = (key: keyof typeof tiles) => {
    const step = Math.max(1, Math.floor(buckets.length / 8));
    const out: number[] = [];
    for (let i = 0; i < buckets.length; i += step) {
      const slice = buckets.slice(i, i + step);
      out.push(slice.reduce((s, b) => s + ((b as any)[key === 'ftds' ? 'ftds' : key] || 0), 0));
    }
    return out;
  };
  tiles.clicks.series   = sample('clicks');
  tiles.installs.series = sample('installs');
  tiles.leads.series    = sample('leads');
  tiles.ftds.series     = sample('ftds');
  tiles.revenue.series  = tiles.ftds.series; // approximate — most revenue comes with FTDs

  return { tiles, buckets };
}

const fmtDate = (ts: number) => new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit' });

export function Dashboard() {
  const [summary, setSummary] = useState<SummaryStats | null>(null);
  const [events, setEvents] = useState<EventRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, e] = await Promise.all([
          api.summary(undefined, 7),
          api.events({ limit: 500 }),
        ]);
        if (cancelled) return;
        setSummary(s);
        setEvents(e);
      } catch (ex: any) {
        if (!cancelled) setErr(ex.message || String(ex));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loading = !summary || !events;

  if (err) {
    return (
      <>
        <PageHeader title="Dashboard" description="Last 7 days across all PWAs" />
        <Alert color="red" icon={<IconAlertCircle size={18} />} title="Failed to load">
          {err}
        </Alert>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <PageHeader title="Dashboard" description="Last 7 days across all PWAs" />
        <Grid mb="md" gutter="md" columns={10}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Grid.Col key={i} span={{ base: 10, xs: 5, md: 2 }}>
              <Skeleton h={150} radius="lg" />
            </Grid.Col>
          ))}
        </Grid>
        <Skeleton h={320} radius="lg" />
      </>
    );
  }

  const agg = aggregate(events);
  const { tiles, buckets } = agg;

  // Reduce the 7×24 hourly buckets into ~56 chart points by sampling every 3h.
  const chartData = [];
  for (let i = 0; i < buckets.length; i += 3) {
    const slice = buckets.slice(i, i + 3);
    chartData.push({
      date: fmtDate(slice[0].ts),
      Clicks:   slice.reduce((s, b) => s + b.clicks,   0),
      Installs: slice.reduce((s, b) => s + b.installs, 0),
      Leads:    slice.reduce((s, b) => s + b.leads,    0),
      FTDs:     slice.reduce((s, b) => s + b.ftds,     0),
    });
  }

  return (
    <>
      <PageHeader
        title="Dashboard"
        description="Last 7 days across all PWAs — refresh by reloading"
      />

      {/* columns={10} lets each col use span=2 → exactly 5 equal tiles per row on md+, 2 per row on xs */}
      <Grid mb="lg" gutter="md" columns={10}>
        <Grid.Col span={{ base: 10, xs: 5, md: 2 }}>
          <MetricTile label="Clicks" {...tiles.clicks} color="brand.6" />
        </Grid.Col>
        <Grid.Col span={{ base: 10, xs: 5, md: 2 }}>
          <MetricTile label="Installs" {...tiles.installs} color="violet.6" />
        </Grid.Col>
        <Grid.Col span={{ base: 10, xs: 5, md: 2 }}>
          <MetricTile label="Leads" {...tiles.leads} color="orange.6" />
        </Grid.Col>
        <Grid.Col span={{ base: 10, xs: 5, md: 2 }}>
          <MetricTile label="FTDs" {...tiles.ftds} color="teal.6" />
        </Grid.Col>
        <Grid.Col span={{ base: 10, xs: 5, md: 2 }}>
          <MetricTile
            label="Revenue"
            {...tiles.revenue}
            color="green.6"
            formatter={(n) => `€${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          />
        </Grid.Col>
      </Grid>

      <Card>
        <Group justify="space-between" mb="md">
          <Title order={4}>Traffic &amp; Conversions</Title>
          <Text c="dimmed" fz="xs">Last 7 days — bucketed every 3h</Text>
        </Group>
        {chartData.length === 0 ? (
          <Stack align="center" justify="center" h={260} c="dimmed">
            <Text>No events recorded yet.</Text>
            <Text fz="xs">Send a click through a PWA and it will show up here.</Text>
          </Stack>
        ) : (
          <AreaChart
            h={300}
            data={chartData}
            dataKey="date"
            withGradient
            withLegend
            curveType="natural"
            series={[
              { name: 'Clicks',   color: 'brand.6' },
              { name: 'Installs', color: 'violet.6' },
              { name: 'Leads',    color: 'orange.6' },
              { name: 'FTDs',     color: 'teal.6' },
            ]}
            tickLine="x"
            gridAxis="xy"
          />
        )}
      </Card>
    </>
  );
}
